import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { validationTypes } from '@pages/product/enums/validationTypes';
import { CustomisableVariables } from '@shared/models/customisable-variables';
import { CustomisableVariablesService } from '@shared/services/customisable-variables.service';
import { ProductHomepageService } from '@shared/services/product-homepage.service';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { BasicInfoDialogCrossComponent } from '@shared/shared/components/basic-info-dialog-cross/basic-info-dialog-cross.component';
import { ReCaptchaComponent } from '@shared/shared/components/re-captcha/re-captcha.component';
import { AuthenticationProvider } from '@shared/shared/enums/authentication-provider.enum';
import { EligibilitiesAndSelfRegEnum } from '@shared/shared/enums/eligibilities-and-selfregs.enum';
import { BaseComponent } from '@shared/shared/helpers/base-component';
import { IEligibilityCheckData } from '@shared/shared/interfaces/eligibility.interface';
import { ISiteSettings } from '@shared/shared/interfaces/site-settings.interfaces';
import { LoggedOutBackgroundService } from '@shared/shared/services/logged-out-background.service';
import { RecaptchaService } from '@shared/shared/services/recaptcha.service';
import { SiteConfigService } from '@shared/shared/services/site-config.service';
import { Angulartics2Segment } from 'angulartics2';
import { Observable, Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'ten-default-eligibility-check',
  templateUrl: './default-eligibility-check.component.html',
  styleUrls: ['./default-eligibility-check.component.scss'],
})
export class DefaultEligibilityCheckComponent extends BaseComponent implements OnInit, OnDestroy {
  @Input() public darkMode = false;
  @Input() public data: IEligibilityCheckData;
  @Output() public closePreloginPopup: EventEmitter<boolean> = new EventEmitter<boolean>(false);

  @ViewChild(ReCaptchaComponent, { static: false })
  public captchaComponent: ReCaptchaComponent;

  public eligibilityForm: UntypedFormGroup;
  public customVariables$: Observable<CustomisableVariables>;
  public siteSettings$: Observable<ISiteSettings> = this.siteSettingsService.fetchSiteSettings();
  public captchaLanguage: string;
  public componentData: any;
  public backgroundImage: string;
  public posterImage = '';
  public videoUrl = '';
  public preloginHomepage = false;

  private oauthActive: boolean;
  private oauthLogInUrl: string;
  public unsubscribe$ = new Subject<void>();
  public clientName: string;
  public eligibilityType: string;
  public showDbsEligibility: boolean;
  public showTMRef1Eligibility: boolean;
  public enableRecaptcha: boolean;

  constructor(
    private productHomepageService: ProductHomepageService,
    private customisableVariablesService: CustomisableVariablesService,
    private siteSettingsService: SiteSettingsService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private reCaptchaService: RecaptchaService,
    private cdr: ChangeDetectorRef,
    public dialog: MatDialog,
    public angulartics2Segment: Angulartics2Segment,
    public loggedOutImageService: LoggedOutBackgroundService,
    public siteConfigService: SiteConfigService
  ) {
    super(siteConfigService, 'homepage');

    this.siteSettings$
      .pipe(
        filter((siteSettings: ISiteSettings) => this.siteSettingsService.settingsRetrieved && !!siteSettings.id),
        take(1)
      )
      .subscribe((siteSettings: ISiteSettings) => {
        this.captchaLanguage = siteSettings.language;
      });

    this.eligibilityForm = new UntypedFormGroup({
      indentifier: new UntypedFormControl('', [Validators.required]),
    });

    this.customVariables$ = this.customisableVariablesService.variables;

    combineLatest([this.loggedOutImageService.heroImage$, this.customVariables$])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([heroImage, customVariables]) => {
        if (customVariables.loggedout_video_link) {
          this.posterImage = heroImage;
          this.videoUrl = customVariables.loggedout_video_link;
        }
      });
  }

  public ngOnInit() {
    this.reCaptchaService.setReCaptcha(true);
    this.siteSettingsService
      .fetchSiteSettings()
      .pipe(
        filter((value) => value !== null),
        takeUntil(this.unsubscribe$),
        distinctUntilChanged()
      )
      .subscribe((siteSettings: ISiteSettings) => {
        this.clientName = siteSettings.fe_configuration.path;
        this.oauthActive =
          siteSettings.authentication_provider?.provider === AuthenticationProvider.visa_lac ? true : false;
        this.oauthLogInUrl = siteSettings.authentication_provider.login_url;
        this.eligibilityType = siteSettings.eligibilities_and_selfregs;
        this.preloginHomepage = siteSettings?.switches?.pre_login_home_page;

        this.showDbsEligibility =
          !!siteSettings && siteSettings.eligibilities_and_selfregs === EligibilitiesAndSelfRegEnum.TM_LINK_REF_1_REF_2;

        this.showTMRef1Eligibility =
          !!siteSettings && siteSettings.eligibilities_and_selfregs === EligibilitiesAndSelfRegEnum.TM_LINK_REF_1;

        this.enableRecaptcha =
          siteSettings?.switches?.enable_recaptchas && siteSettings?.switches?.enable_eligibility_recaptcha;

        this.setValidation();

        this.cdr.detectChanges();
      });
  }

  private getClientValidationRules(): any[] {
    const rules =
      this.componentData && this.componentData.eligibility && !!this.componentData.eligibility.validationRules
        ? this.componentData.eligibility.validationRules
        : null;

    const validationRules = [Validators.required];

    if (rules === null) {
      return validationRules;
    }

    rules.forEach((rule) => {
      switch (rule.type) {
        case validationTypes.Pattern:
          validationRules.push(Validators.pattern(rule.value));
          break;
        case validationTypes.MinLength:
          validationRules.push(Validators.minLength(rule.value));
          break;
        case validationTypes.MaxLength:
          validationRules.push(Validators.maxLength(rule.value));
          break;
        case validationTypes.Email:
          validationRules.push(Validators.email);
          break;
        default:
          break;
      }
    });

    return validationRules;
  }

  private setValidation() {
    if (this.showTMRef1Eligibility) {
      this.eligibilityForm.get('indentifier').setValidators(this.getClientValidationRules());
    }
  }

  public getCaptchaToken(captchaToken?: string) {
    if (this.showDbsEligibility) {
      this.productHomepageService.checkEligibility(
        {
          identifier: this.eligibilityForm.get('indentifier').value.replace(/\s/g, ''),
          identifier2: this.eligibilityForm.get('indentifier2').value.replace(/\s/g, ''),
          captchaToken,
        },
        this.eligibilityType
      );
    } else {
      this.productHomepageService.checkEligibility(
        {
          identifier: 'jade_id',
          value: this.eligibilityForm.get('indentifier').value.replace(/\s/g, ''),
          captchaToken,
        },
        this.eligibilityType
      );
    }
    this.closePreloginPopup.emit(true);
  }

  public setFormData(formData: any) {
    this.eligibilityForm = formData;
  }

  /**
   * Dispatch the card number enterted by the user to be used in the relavant API call.
   * Called the user clicks onto the 'Check Eligibility and Register' button within the template
   */
  public checkEligibility() {
    if (this.captchaComponent && this.enableRecaptcha) {
      this.captchaComponent.execute();
    } else {
      this.getCaptchaToken();
    }
  }

  public openDialog() {
    const dialogUnsubscribe$ = new Subject<void>();
    this.customVariables$
      .pipe(
        filter((data) => data['eligibility_popup_title'] !== '' && data['eligibility_popup_body'] !== ''),
        distinctUntilChanged(),
        takeUntil(dialogUnsubscribe$)
      )
      .subscribe((data) => {
        const direction = document.querySelector('html')?.dir ? document.querySelector('html').dir : 'ltr';
        this.showDialog(data['eligibility_popup_title'], data['eligibility_popup_body'], direction);

        dialogUnsubscribe$.next();
        dialogUnsubscribe$.complete();
      });
  }

  private showDialog(title: string, body: any, direction: any) {
    this.dialog.open(BasicInfoDialogCrossComponent, {
      panelClass: 'eligility-check-' + this.clientName,
      data: {
        title: title,
        body: body,
        closeBtn: false,
      },
      direction,
    });
  }

  public onClick() {
    const redirectUrl = this.activatedRoute.snapshot.queryParams['returnUrl'];
    if (this.oauthActive) {
      window.location.href = `${window.location.origin}${this.oauthLogInUrl}?onwardUrl=${redirectUrl || '/user/login'}`;
    } else {
      const loginPath = `/user/login${redirectUrl ? `?returnUrl=${encodeURIComponent(redirectUrl)}` : ''}`;

      if (redirectUrl !== '') {
        window.location.href = loginPath;
        return;
      }

      this.router.navigate(['/user/login']);
    }
  }

  public ngOnDestroy() {
    this.reCaptchaService.setReCaptcha(false);
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
